import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

export interface NotificationItem {
  id: string;
  title: string;
  message: string;
  type?: string;
  link?: string | null;
  isRead: boolean;
  createdAt: string;
  priority?: string | null;
  eventType?: string | null;
  referenceType?: string | null;
  referenceId?: string | null;
  readAt?: string | null;
}

function priorityDotColor(priority?: string | null): string {
  switch (priority) {
    case "critical": return "bg-red-500";
    case "high": return "bg-amber-500";
    case "low": return "bg-gray-400";
    default: return "bg-blue-500";
  }
}

const EVENT_ICONS: Record<string, string> = {
  "order.created": "📦",
  "order.approved": "✅",
  "order.rejected": "❌",
  "order.shipped": "🚚",
  "order.delivered": "📥",
  "order.edited": "✏️",
  "order.cancelled": "🚫",
  "cycle.deadline_approaching": "⏰",
  "cycle.deadline_overdue": "🔴",
  "cycle.all_submitted": "📋",
  "cycle.link_generated": "🔗",
  "cycle.supplier_confirmed": "✅",
  "cycle.supplier_adjusted": "⚠️",
  "cycle.supplier_issue": "🚨",
  "return.created": "↩️",
  "return.approved": "✅",
  "return.rejected": "❌",
  "return.shipped": "📦",
  "return.received": "📥",
  "return.quarantine_aged": "🔍",
  "return.writeoff_pending": "🗑️",
  "transfer.created": "🚚",
  "transfer.shipped": "🚚",
  "transfer.received": "📥",
  "transfer.cancelled": "🚫",
  "inventory.count_due_tomorrow": "📋",
  "inventory.count_overdue": "🔴",
  "inventory.stocktake_due": "📊",
  "procurement.receipt_confirmed": "📥",
};

function eventTypeIcon(eventType?: string | null): string {
  if (!eventType) return "🔔";
  return EVENT_ICONS[eventType] || "🔔";
}

export function timeAgo(dateStr: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return t("notifMisc.justNow");
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return t("notifMisc.minutesShort", { n: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t("notifMisc.hoursShort", { n: hours });
  const days = Math.floor(hours / 24);
  return t("notifMisc.daysShort", { n: days });
}

interface NotificationRowProps {
  notification: NotificationItem;
  compact?: boolean;
  onRead: (id: string) => void;
  onNavigate: (link: string) => void;
}

export function NotificationRow({ notification, compact, onRead, onNavigate }: NotificationRowProps) {
  const { t, i18n } = useTranslation();
  const n = notification;
  // Only this application-owned event title is localized. Stored notification
  // bodies contain source references and user notes and remain untouched.
  const title = n.eventType === "cash_attention_assigned"
    ? (i18n.language?.split("-")[0] === "ar" ? "تم إسناد بند نقدي" : "Cash item assigned")
    : n.title;
  const isUnread = !n.isRead;

  const handleClick = () => {
    if (isUnread) onRead(n.id);
    if (n.link) onNavigate(n.link);
  };

  return (
    <div
      onClick={handleClick}
      data-testid={`notification-item-${n.id}`}
      className={cn(
        "flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-border/50 last:border-0",
        isUnread ? "bg-primary/5 hover:bg-primary/10" : "hover:bg-muted/50"
      )}
    >
      <div className="flex flex-col items-center gap-1 pt-0.5 flex-shrink-0">
        <span className={cn("w-2 h-2 rounded-full", priorityDotColor(n.priority))} />
        <span className="text-sm leading-none">{eventTypeIcon(n.eventType)}</span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className={cn("text-sm leading-snug truncate", isUnread ? "font-semibold" : "font-medium")}>
            {title}
          </p>
          {isUnread && <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />}
        </div>
        <p className={cn("text-xs mt-0.5 text-muted-foreground", compact && "line-clamp-2")} dir="auto">
          {n.message}
        </p>
      </div>

      <span className="text-xs text-muted-foreground font-mono whitespace-nowrap pt-0.5 flex-shrink-0">
        {timeAgo(n.createdAt, t)}
      </span>
    </div>
  );
}
